import {
  NJButton,
  NJHeading,
  NJInlineMessage,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { ConfirmModal } from "@/components/ConfirmModal";
import { StatusBadge } from "@/components/StatusBadge";
import { MetaTagList } from "@/components/store/MetaTagList";
import { MarkdownMessage } from "@/features/chat/MarkdownMessage";
import { useAdminAgent, useDeleteAdminAgent } from "./useAdminAgents";

/** Read-only admin agent detail with Edit / Delete actions (US-11). */
export function AdminAgentDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading, isError } = useAdminAgent(slug);
  const deleteAgent = useDeleteAdminAgent();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <NJSpinner aria-label="Loading the agent" />
      </div>
    );
  }

  if (isError || !agent) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton to="/admin/agents" label="Back to Agents" />
        <NJInlineMessage variant="error">
          Could not load this agent.
        </NJInlineMessage>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <BackButton to="/admin/agents" label="Back to Agents" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <NJHeading as="h1" scale="lg">
            {agent.name}
          </NJHeading>
          <StatusBadge status={agent.status} />
        </div>
        <div className="flex gap-2">
          <NJButton
            label="Edit"
            icon="edit"
            onClick={() => navigate(`/admin/agents/${agent.slug}/edit`)}
          />
          <NJButton
            label="Delete agent"
            variant="destructive"
            emphasis="subtle"
            disabled={deleteAgent.isPending}
            onClick={() => setConfirmOpen(true)}
          />
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Delete agent"
        isPending={deleteAgent.isPending}
        onConfirm={() =>
          deleteAgent.mutate(agent.slug, {
            onSuccess: () => navigate("/admin/agents"),
          })
        }
      >
        Delete agent <strong>{agent.name}</strong>? This also removes its access
        requests, sessions and groups.
      </ConfirmModal>

      <section className="grid gap-4 rounded-xl border border-nj-neutral-subtle-default p-6 sm:grid-cols-2">
        <Field label="Slug" value={agent.slug} />
        <Field label="Maintainer" value={agent.maintainer} />
        <Field
          label="Short description"
          value={agent.shortDescription || "-"}
        />
        <Field label="Context window" value={`${agent.contextWindow} tokens`} />
        <Field label="API endpoint" value={agent.apiEndpoint || "-"} />
        <Field label="Auth type" value={agent.authType} />
        <Field label="Usage members" value={String(agent.usageMembersCount)} />
        <Field
          label="Validator members"
          value={String(agent.validatorMembersCount)}
        />
      </section>

      <div className="rounded-xl border border-nj-neutral-subtle-default p-6">
        <MetaTagList label="Use cases" items={agent.useCases ?? []} />
      </div>

      {agent.fullDescription && (
        <section className="flex flex-col gap-2 rounded-xl border border-nj-neutral-subtle-default p-6">
          <NJHeading as="h2" scale="sm">
            Full description
          </NJHeading>
          <MarkdownMessage content={agent.fullDescription} />
        </section>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <NJText
        as="span"
        scale="xs"
        className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
      >
        {label}
      </NJText>
      <NJText as="span" scale="md" className="break-words">
        {value}
      </NJText>
    </div>
  );
}
